import { observable, computed, action, makeObservable } from 'src_common/common/mobx-wrapper';
import { FormModel, FormModelType, Result } from './FormModel';

class BoxValue<K> {
    private readonly initValue: K;
    @observable public value: K;
    @observable private visited: boolean;
    @observable private touched: boolean = false;

    public constructor(value: K) {
        this.initValue = value;
        this.value = value;
        this.visited = false;
        makeObservable(this);
    }

    @action public setAsVisited = (): void => {
        this.visited = true;
    };

    public isVisited(): boolean {
        return this.visited;
    }

    @action public setIsTouched = (): void => {
        this.touched = true;
    };

    public isTouched(): boolean {
        return this.touched;
    }

    @action public resetIsTouched = (): void => {
        this.touched = false;
    };

    @action public resetIsVisited = (): void => {
        this.visited = false;
    };

    @action public reset(): void {
        this.value = this.initValue;
        this.visited = false;
        this.touched = false;
    }
}

export class FormInputState<K, M> implements FormModelType<M> {
    private readonly box: BoxValue<K>;
    private readonly model: FormModel<M>;

    private constructor(box: BoxValue<K>, model: FormModel<M>) {
        this.box = box;
        this.model = model;
        makeObservable(this);
    }

    public static new<K>(value: K): FormInputState<K, K> {
        const box = new BoxValue(value);
        const model = new FormModel([box], (): Result<K> => Result.createOk(box.value));

        return new FormInputState(box, model);
    }

    @action public setValue = (value: K): void => {
        this.box.value = value;
    };

    @action public setValueOnChange = (value: K): void => {
        if (this.value !== value && this.isTouched() === false) {
            this.box.setIsTouched();
        }

        this.setValue(value);
    };

    @action public resetIsTouched = (): void => {
        this.box.resetIsTouched();
    };

    @action public resetIsVisited = (): void => {
        this.box.resetIsVisited();
    };

    @computed public get value(): K {
        return this.box.value;
    }

    public isVisited(): boolean {
        return this.box.isVisited();
    }

    public isTouched(): boolean {
        return this.box.isTouched();
    }

    /**
     * @deprecated
     */
    public toModel(): FormModel<M> {
        return this.model;
    }

    public map<M2>(convert: (value: M) => Result<M2>): FormInputState<K, M2> {
        const model2 = new FormModel([this.box], (): Result<M2> => this.model.result.map(convert));

        return new FormInputState(this.box, model2);
    }

    //implements FormModelType
    public get result(): Result<M> {
        const result = this.model.result;

        if (this.box.isVisited()) {
            return result;
        }

        return result.muteErrors();
    }

    @computed public get errorsForView(): Array<string> {
        return this.result.errors();
    }

    @action public setAsVisited = (): void => {
        this.model.setAsVisited();
    };

    @action public reset = (): void => {
        this.model.reset();
    };
}
